/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classnames from 'classnames';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { Module, Button } from '@msdyn365-commerce-modules/utilities';
import { IDlvryNavigationMenuViewProps } from './dlvry-navigation-menu';
import { productsMenuStyle } from './dlvry-navigation-menu.props.autogenerated';

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export default (props: IDlvryNavigationMenuViewProps) => {
    const { context, baseClassName, productMenuItem } = props;

    const [productsDropDownOpen, setProductsDropDownOpen] = React.useState(false);
    const [hasMultiRowClass, setHasMultiRowClass] = React.useState(false);
    const [dropdownStart, setDropdownStart] = React.useState<number | undefined>(undefined);
    const [showDropdown, setShowDropdown] = React.useState(false);

    const [dropDownOpenIndex, setDropDownOpenIndex] = React.useState<number | undefined>(undefined);

    const listRef = React.useRef<HTMLUListElement>(null);

    const menuDropdownRef = React.useRef<HTMLUListElement>(null);
    const menuDropdownButtonRef = React.useRef<HTMLButtonElement>(null);

    const onProductsMenuClickHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setDropDownOpenIndex(undefined);
        setProductsDropDownOpen(!productsDropDownOpen);
    };

    const onDropDownMenuClickHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        // Get data-index from the clicked element
        const target = event.currentTarget as HTMLAnchorElement;
        const index = target.getAttribute('data-index');

        if (index) {
            const indexNumber = parseInt(index, 10);
            setDropDownOpenIndex(indexNumber);
        }

        // Close products dropdown if it is open
        setProductsDropDownOpen(false);
    };

    React.useEffect(() => {
        // When clicking outside the dropdown, close it
        const handleClick = (event: MouseEvent) => {
            if ((event.target as HTMLElement).closest(`.${baseClassName}__list__item.dropdown`)) {
                // Do nothing if clicking inside the dropdown
                return;
            }
            setProductsDropDownOpen(false);
            setDropDownOpenIndex(undefined);
        };
        document.addEventListener('click', handleClick);
    }, []);

    React.useEffect(() => {
        const onResize = () => {
            if (!listRef.current || listRef.current.children.length <= 1) {
                return;
            }

            const firstOffsetTop = (listRef.current.children[0] as HTMLElement).offsetTop;

            for (let i = 0; i < listRef.current.children.length; i++) {
                const element = listRef.current.children[i] as HTMLElement;
                if (element.offsetTop !== firstOffsetTop) {
                    setDropdownStart(i);
                    setHasMultiRowClass(true);
                    return;
                }
            }

            setDropdownStart(undefined);
            setHasMultiRowClass(false);
        };

        onResize();

        let observer: ResizeObserver | undefined;

        if (ResizeObserver) {
            observer = new ResizeObserver(() => {
                onResize();
            });
            observer.observe(listRef.current as Element);
        } else {
            console.warn('ResizeObserver not supported, using window resize event instead');
            window.addEventListener('resize', onResize);
        }

        return () => {
            if (observer) {
                observer.disconnect();
            } else {
                window.removeEventListener('resize', onResize);
            }
        };
    }, []);

    React.useEffect(() => {
        const onClickOutside = (event: MouseEvent) => {
            if (menuDropdownRef.current && menuDropdownButtonRef.current) {
                if (!menuDropdownRef.current.contains(event.target as Node)
                    && menuDropdownButtonRef.current !== event.target
                    && !menuDropdownButtonRef.current.contains(event.target as Node)
                ) {
                    setShowDropdown(false);
                }
            }
        };

        document.addEventListener('click', onClickOutside);

        return () => {
            document.removeEventListener('click', onClickOutside);
        };
    }, []);

    const menuItems = props.menuItems.map((item, index) => {
        if (item.title === '<SEPARATOR>') {
            return null;
            // We don't support separator in main menu for now because of overflow menu and being unsure how to handle it
            // return <li key={index} className={`${baseClassName}__list__separator`} />;
        }

        if (item.children && item.children.length > 0) {
            return (
                <li key={index} className={`${baseClassName}__list__item dropdown`}>
                    <a className={classnames(`${baseClassName}__list__item__link`, { highlight: item.highlight })} data-index={index} onClick={onDropDownMenuClickHandler}>
                        {item.title}
                    </a>
                    <ul className={`${baseClassName}__list__item__sub-list`} data-open={dropDownOpenIndex === index}>
                        {item.children.map((child, childIndex) => {
                            if (child.title === '<SEPARATOR>') {
                                return <li key={childIndex} className={`${baseClassName}__list__item__sub-list__separator`} />;
                            }

                            return (
                                <li key={childIndex} className={`${baseClassName}__list__item__sub-list__item`}>
                                    <a href={child.url || '#'} data-index={index} onClick={undefined}>{child.title}</a>
                                </li>
                            );
                        })}
                    </ul>
                </li>
            );
        }

        return (
            <li key={index} className={`${baseClassName}__list__item`}>
                <a className={classnames(`${baseClassName}__list__item__link`, { highlight: item.highlight })} href={item.url}>{item.title}</a>
            </li>
        );
    });

    if (productMenuItem.children
        && productMenuItem.children.length > 0
        && (productMenuItem.showUnauthorized || context.request.user.isAuthenticated)
    ) {
        const style = props.config.productsMenuStyle;
        const productsMenuItemPosition = props.config.productsMenuItemPosition || 1;
        const productMenuItemIndex = clamp((productsMenuItemPosition - 1), 0, props.menuItems.length); // Minus one the config value so it's zero-based
        // Insert products menu item
        menuItems.splice(productMenuItemIndex, 0, (
            <li key='ProductsMenu' className={`${baseClassName}__list__item dropdown`}>
                <a className={`${baseClassName}__list__item__link`} onClick={onProductsMenuClickHandler}>
                    {productMenuItem.title}
                </a>
                <ul className={classnames(`${baseClassName}__list__item__sub-list`, { grid: style === productsMenuStyle.grid })} data-open={productsDropDownOpen}>
                    {productMenuItem.children && productMenuItem.children.map((child, childIndex) => {
                        if (child.title === '<SEPARATOR>') {
                            return <li key={childIndex} className={`${baseClassName}__list__item__sub-list__separator`} />;
                        }

                        return (
                            <li key={childIndex} className={`${baseClassName}__list__item__sub-list__item`}>
                                <a href={child.url || '#'}>{child.title}</a>
                            </li>
                        );
                    })}
                </ul>
            </li>
        ));
    }

    const [documentLoaded, setDocumentLoaded] = React.useState(false);

    React.useEffect(() => {
        setDocumentLoaded(true);
    }, []);

    const oboCustomerAccountNumber: { can: string | undefined } = Msdyn365.getCustomerAccountNumber(context.request) as any;
    const showCartIcon = context.request.user.isAuthenticated && (context.request.user.isOBORequest ? !!oboCustomerAccountNumber.can : true);

    return (
        <Module {...props.navigationMenu}>
            <ul
                className={classnames(`${baseClassName}__list`, { 'multi-row': hasMultiRowClass })}
                ref={listRef}
                style={!documentLoaded ? { overflow: 'hidden' } : undefined} // Hide overflow on SSR to prevent layout shift
            >
                {menuItems.map((item, index) => {
                    if (!item || dropdownStart === undefined || index < dropdownStart) {
                        return item;
                    }

                    // Add drop class to items that should be in the dropdown
                    return React.cloneElement(item, { ...item.props, className: `${item.props.className} drop` });
                })}
            </ul>
            {props.customerName}
            {showCartIcon && props.slots.cartIcon}
            {dropdownStart !== undefined && dropdownStart > 0 && (
                <>
                    <div
                        onMouseOver={() => {
                            setShowDropdown(true);
                        }}
                        onMouseLeave={() => {
                            setShowDropdown(false);
                        }}
                        style={{ position: 'relative' }}
                    >
                        <Button
                            className={classnames(`${baseClassName}__list__dropdown__button`, { open: showDropdown })}
                            innerRef={menuDropdownButtonRef}
                            onClick={() => {
                                setShowDropdown(true);
                            }}
                            aria-label={props.resources.navigationOverflowButtonLabel}
                        >
                            {/* {props.resources.navigationOverflowButtonLabel} */}
                        </Button>
                        <ul className={classnames(`${baseClassName}__list__dropdown overflow`, { show: showDropdown })} ref={menuDropdownRef}>
                            {menuItems.slice(dropdownStart)}
                        </ul>
                    </div>
                </>
            )}
        </Module>
    );
};
